import Vue from 'vue';
import Vuex from 'vuex';

import Service from "./services/service";
import UsageService from "./services/usage";
import ArticleService from "./services/article";
import axios from 'axios';
import { API_PATH } from '../config/config';
import TextHelpers from './util/helper-functions';

class Deferred {
	constructor() {
		this.promise = new Promise((resolve, reject) => {
			this.reject = reject
			this.resolve = resolve
		})
	}
}

Vue.use(Vuex);

export default new Vuex.Store({
	state: {
		services: [],
		loading: [],
		errors: [],
		servicesHealth: [],
		usage: {},
		articles: {},
		virtualCash: 0,
		userPrices: {},
		consoleReady$: new Deferred(),
		authData: {
			tokenData: null,
			user: null,
			isDifferentUser: false,
			isAuthenticated: false,
			isAdmin: false,
			isDeveloper: false,
			canNavigate: false,
			lastUserId: null,
		}
	},
	getters: {
		loading: (state) => (name) => {
			return state.loading.includes(name);
		},
		error: (state) => (name) => {
			let error = state.errors.find(e => e.name === name);

			return error ? error.err : null;
		},
		services: (state) => {
			return [...state.services];
		},
		downServices: (state) => {
			let ds = state.servicesHealth.filter(s => (s.be === 'DOWN' && s.be != 'N/A') || (s.fe === 'DOWN' && s.fe != 'N/A'));
			return ds;
		},
		serviceHealth: (state) => {
			return [...state.servicesHealth];
		},
		usage: (state) => {
			return { ...state.usage };
		},
		articles: (state) => {
			return { ...state.articles };
		},
		virtualCash: (state) => {
			return state.virtualCash;
		},
		groupedUserPrices: (state) => {
			let services = state.services,
				grpPrices = [];

			for (const s in state.userPrices) {
				let service = services.find((ser) => ser.id === s);
				let group = grpPrices.find((gp) => gp.group === service.group);
				if (group) {
					group.services.push({
						name: TextHelpers.kebabToPascalCase(s),
						prices: state.userPrices[s],
					});
				} else {
					grpPrices.push({
						group: service.group,
						services: [
							{
								name: TextHelpers.kebabToPascalCase(s),
								prices: state.userPrices[s],
							},
						],
					});
				}
			}
			return grpPrices.sort((a, b) => {
				let aGrp = a.group.toLowerCase();
				let bGrp = b.group.toLowerCase();
				return (aGrp < bGrp) ? -1 : (aGrp > bGrp) ? 1 : 0;
			});
		},
		isAuthenticated: (state) => {
			return state.authData.isAuthenticated;
		},
		isDeveloper: (state) => {
			return state.authData.isDeveloper;
		},
		canNavigate: (state) => {
			return state.authData.canNavigate;
		}
	},
	actions: {
		fetchServices({ commit, dispatch }) {
			commit('SET_LOADING_TRUE', 'service-health');

			return axios.get(API_PATH + '/services')
				.then((res) => {
					commit('REMOVE_ERROR', 'services');
					commit('SET_SERVICES', res.data.services);
					dispatch('fetchServicesHealth');
					dispatch('fetchUserPrices');
				}).catch((err) => {
					commit('SET_LOADING_FALSE', 'service-health');
					commit('ADD_ERROR', { name: 'services', err });
				});
		},
		async fetchServicesHealth({ commit, state }) {
			let results = [];
			let serviceCheck = state.services.map((s) => {
				return {
					id: s.id,
					fe: s.health.fe,
					be: s.health.be,
					feInfo: s.info.fe,
					beInfo: s.info.be
				}
			});
			commit('SET_LOADING_TRUE', 'services-health');

			for (let i = 0; i < serviceCheck.length; i++) {
				const sc = serviceCheck[i];
				let result = { id: sc.id };

				for (const k in sc) {
					if (k != 'id') {
						try {
							result[k] = (await Service.instance().healthCheck(sc[k])).data;
						} catch (err) {
							result[k] = {
								status: err.message === 'Unavailable' ? 'N/A' : 'DOWN',
								tag: 'n/a'
							}
						}
					}
				}
				results.push(result);
			}
			commit('SET_LOADING_FALSE', 'service-health');
			commit('SET_SERVICE_HEALTH', results);
		},
		fetchUsage({ commit }) {
			commit('SET_LOADING_TRUE', 'usage');
			return UsageService.instance()
				.getUsage()
				.then((data) => {
					commit('SET_LOADING_FALSE', 'usage');
					commit('REMOVE_ERROR', 'usage');
					commit('SET_USAGE', data.data);
				})
				.catch((err) => {
					commit('SET_LOADING_FALSE', 'usage');
					commit('ADD_ERROR', { name: 'usage', err });
				});
		},
		fetchArticles({ commit }) {
			commit('SET_LOADING_TRUE', 'articles');
			return ArticleService.instance()
				.getHowTo()
				.then((data) => {
					const ids = data.data.map(a => a.id);
					ArticleService.instance().getListByIds(ids)
						.then((data) => {
							commit('SET_LOADING_FALSE', 'articles');
							commit('REMOVE_ERROR', 'articles');
							commit('SET_ARTICLES', data.data);
						})
						.catch((err) => {
							commit('SET_LOADING_FALSE', 'articles');
							commit('ADD_ERROR', { name: 'articles', err });
						});
				})
				.catch((err) => {
					commit('SET_LOADING_FALSE', 'articles');
					commit('ADD_ERROR', { name: 'articles', err });
				});
		},
		fetchVirtualCash({ commit }) {
			return UsageService.instance().getVirtualCash()
				.then((result) => {
					commit('REMOVE_ERROR', 'virtualcash');
					commit('SET_VIRUTAL_CASH', result.data);
				}).catch((err) => {
					commit('ADD_ERROR', { name: 'virtualcash', err });
				});
		},
		fetchUserPrices({ commit }) {
			commit('SET_LOADING_TRUE', 'userprices');
			return axios.get(API_PATH + '/console/v1/public/pricing')
				.then((result) => {
					commit('SET_LOADING_FALSE', 'userprices');
					commit('SET_USER_PRICES', result.data);
				}).catch((err) => {
					commit('SET_LOADING_FALSE', 'userprices');
					commit('ADD_ERROR', { name: 'userprices', err });
				});
		},
		setTokenData({ commit }, tokenData) {
			commit('SET_TOKEN_DATA', tokenData);
		},
		setAuthenticatedUserData({ commit }, data) {
			commit('SET_AUTHENTICATED_USER_DATA', data);
		},
		clearAuthData({ commit }) {
			commit('CLEAR_AUTH_DATA');
		},
		setConsoleReady({ commit }) {
			commit('SET_CONSOLE_READY');
		}
	},
	mutations: {
		SET_LOADING_TRUE(state, name) {
			state.loading.push(name);
		},
		SET_LOADING_FALSE(state, name) {
			state.loading = state.loading.filter(l => l != name);
		},
		ADD_ERROR(state, error) {
			state.errors.push(error);
		},
		REMOVE_ERROR(state, name) {
			state.errors = state.errors.filter(e => e.name != name);
		},
		SET_SERVICES(state, services) {
			state.services = services;
		},
		SET_SERVICE_HEALTH(state, servicesHealth) {
			let shArr = [];
			servicesHealth.forEach(sh => {
				let service = state.services.find(s => s.id === sh.id);
				shArr.push({
					name: service.name,
					uri: service.uri,
					be: sh.be.status,
					fe: sh.fe.status,
					tag_be: sh.beInfo.tag,
					tag_fe: sh.feInfo.tag,
					commit_be: sh.beInfo.commit,
					commit_fe: sh.feInfo.commit,
					build_date_be: sh.beInfo.build_date,
					build_date_fe: sh.feInfo.build_date
				});
			});
			state.servicesHealth = shArr;
		},
		SET_USAGE(state, usage) {
			state.usage = usage;
		},
		SET_ARTICLES(state, articles) {
			state.articles = articles;
		},
		SET_VIRUTAL_CASH(state, vCash) {
			state.virtualCash = vCash;
		},
		SET_USER_PRICES(state, prices) {
			state.userPrices = prices;
		},
		SET_TOKEN_DATA(state, tokenData) {
			state.authData = {
				...state.authData,
				tokenData: tokenData
			};
		},
		SET_AUTHENTICATED_USER_DATA(state, userData) {
			state.authData = {
				...state.authData,
				isAuthenticated: true,
				isDifferentUser: state.authData.lastUserId !== null && state.authData.lastUserId !== userData.user.userId,
				user: userData.user,
				isAdmin: userData.isAdmin,
				isDeveloper: userData.isDeveloper,
				canNavigate: userData.canNavigate,
			};
		},
		CLEAR_AUTH_DATA(state) {
			state.authData = {
				...state.authData,
				lastUserId: state.authData.user ? state.authData.user.userId : null,
				tokenData: null,
				user: null,
				isDifferentUser: false,
				isAuthenticated: false,
				isAdmin: false,
				isDeveloper: false,
				canNavigate: false,
			}
		},
		SET_CONSOLE_READY(state) {
			state.consoleReady$.resolve(true);
		}
	}
});
